import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cx } from "../control/ui/primitives";
import { GoonlordsBarMark } from "../control/ui/Logo";
import { usePublicSettings } from "../lib/publicSettings";
import { homeHref } from "../lib/lastHomeView";
import { focusablesIn } from "../lib/focusReturn";
import { NewBadge } from "./NewBadge";
import { HOW_TO_LINK } from "./siteLinks";
import { MASK } from "../lib/replayMask";
import {
  IconBook,
  IconEye,
  IconBag,
  IconEyeOff,
  IconHome,
  IconLayout,
  IconMenu,
  IconMonitor,
  IconRemote,
  IconSearch,
  IconStack,
  IconSparkle,
  IconUser,
} from "../control/ui/icons";
import { useShopOpen } from "../lib/shopOpen";

/**
 * THE one top bar. Every user-facing page shares it — the landing, the control pages,
 * the legal pages, and both screen surfaces. The site had grown four: TopNav on the
 * control panel, ScreenBar on a screen, a hand-rolled header on the landing (carrying the
 * third copy of the signed-in/signed-out fork AccountControl was extracted to kill), and
 * the Goontroller's deliberate nothing. All four are this file now — the remote wears it
 * code-less (a controller must never be a claimable screen; see SiteChrome) — and only
 * the admin keeps its special-purpose layout, on purpose.
 *
 * THREE FIXED POSITIONS, ONE INJECTABLE MIDDLE.
 *
 *   Left:   the mark and the wordmark, linking home. A screen is often somebody else's
 *           television and this bar is the first thing anyone sees of the app; the brand
 *           is the one fixed point while everything else changes with the page.
 *   Middle: whatever the page is about. A screen injects its now-playing cluster (name,
 *           Edit, fullscreen, the goon session); every other page defaults to the three
 *           section links. Centred between its neighbours, `min-w-0` so a long view name
 *           truncates rather than pushing the account control off the edge.
 *   Right:  the screen-code bubble, then who you are (AccountControl). The code rides
 *           EVERY page — any tab of this site is a claimable screen now — so it lives in
 *           the bar's fixed chrome rather than in any page's own furniture. Claimed
 *           screens wear one too: theirs ADOPTS the device in place (ownership moves,
 *           playback does not) instead of minting a new one. See usePendingScreen.
 *
 * OPACITY IS A DIAL, NOT A THEME. The background is always black — what a television's
 * own overlays use, readable across a room against moving video — and `opacity` (0..1)
 * is how much of it a page wants: the landing runs 0 so the hero art shows through, a
 * screen runs slightly transparent, everything else near-solid. The border and blur
 * follow it out: a hairline under a fully transparent bar would draw the edge of a box
 * that is not there.
 *
 * NO AUTO-FADE, NO COLLAPSE, NO EXIT. ScreenBar faded after an idle clock and folded to
 * a tab; both are gone — and so is the fullscreen `hidden` that removed the bar
 * entirely: the bubble is a claimed screen's handover path, and fullscreen is when a TV
 * is most likely to be watched from across a room. The bar is simply always present.
 *
 * PRESENTATIONAL ON PURPOSE, like the ScreenBar it replaces: `account` and `center`
 * arrive as nodes and the code as data, so the bar renders standalone in its check with
 * no session store and no socket. The default middle does need a router — the links are
 * links — which the check provides the same way home-surface.mts always has.
 */

/** The screen-code bubble's state: the code (or a placeholder), and whether the big
 *  code card is up. Without `onOpen` the bubble is the way to the card and the way
 *  back out; with it, the chip body is a DOOR instead — the non-screen pages send the
 *  click to /screen, where this same code is already playing the part. */
export interface CodeBubble {
  text: string;
  shown: boolean;
  onToggle: () => void;
  /** When set, the chip BODY does this instead of toggling the code card. */
  onOpen?: () => void;
}

function HomeLogoLink() {
  const to = homeHref();
  return (
    <NavLink to={to} end={to === "/"} className="flex shrink-0 items-center sm:pr-1">
      <GoonlordsBarMark />
    </NavLink>
  );
}

export function SiteBar({
  opacity = 0.85,
  overlay = false,
  center,
  code,
  faps,
  account,
  menu,
  back,
}: {
  /** How much black behind the bar, 0..1. The landing wants 0; screens ~0.85. */
  opacity?: number;
  /** Absolute over the content (a screen's video) rather than sticky in the flow. */
  overlay?: boolean;
  /** The page's middle. Absent = the three section links. */
  center?: ReactNode;
  code?: CodeBubble | null;
  /**
   * The FAPS pill, as a NODE — this bar is presentational and is rendered standalone by
   * tools/checks/site-bar.mts, so it must never read the wallet itself. The chrome
   * that owns the FAPS dialog hands the pill in, the same way it hands in `account`.
   */
  faps?: ReactNode;
  account?: ReactNode;
  /**
   * Extra entries for the collapsed menu, under the section links. This is where a
   * page's controls go when a phone cannot fit them in the bar — the wired chrome puts
   * the sign-in/profile actions here, and a screen can collapse its own options into
   * the same list rather than growing a second menu.
   */
  menu?: ReactNode;
  /**
   * A way back to where this page was opened from, beside the mark. Absent on every page
   * that does not ask for one, so the bar is unchanged everywhere else — today only a
   * `/screen` opened from a Quick setup on Views hands one in (Casey 2026-10-09 #2).
   */
  back?: ReactNode;
}) {
  const solid = opacity > 0;
  return (
    <header
      /* THE CAMERA. The viewport is `viewport-fit=cover`, so on a phone the page runs
         under the cutout — right for edge-to-edge video, and the deal that comes with it
         is that chrome at an edge pads itself by the inset the phone reports for that edge.
         The bottom already did (the thumb bar, the footer); the top and sides did not, so
         in fullscreen the record dot and the mute sat under the camera (2026-09-18, a
         photo of a phone). Each padding is the bar's own plus the inset — zero on a desk
         and on a phone without a cutout, so nothing there moves. */
      className={cx(
        /* Above Goon Fuel (fixed z-40). Same-z left Fuel — mounted later in ControlApp —
           painting over SiteMenu's lower rows (Remote etc.) while expanded (Casey
           2026-09-28 evening / #8 residual). */
        "top-0 z-50 flex items-center gap-x-1 gap-y-1 py-2 sm:gap-x-2",
        /* The last-resort phone layout — see the centre group below. */
        "max-[349px]:flex-wrap",
        "pt-[calc(0.5rem_+_env(safe-area-inset-top))]",
        "pl-[calc(0.75rem_+_env(safe-area-inset-left))] pr-[calc(0.75rem_+_env(safe-area-inset-right))]",
        "sm:pl-[calc(1rem_+_env(safe-area-inset-left))] sm:pr-[calc(1rem_+_env(safe-area-inset-right))]",
        overlay ? "absolute inset-x-0" : "sticky",
        solid ? "border-b border-white/10 backdrop-blur" : "",
      )}
      style={{ backgroundColor: `rgba(0, 0, 0, ${opacity})` }}
    >
      {/* THREE PARTS, AND THE SIDES BALANCE. Both flanks are `flex-1 basis-0`, so they
          split the leftover space equally and the middle sits at the centre of the
          VIEWPORT — not merely centred between a narrow brand and a wide account group,
          which drifted it visibly left of the page's own centre line. */}
      <div className="flex flex-1 basis-0 items-center justify-start">
        {/* The mark goes Home — last Infinite Scroll / wall when this tab has one, else
            bare `/`. A bare `/` from Browse used to redraw the front door and lose the
            mode (Casey 2026-10-07 AM #1). */}
        <HomeLogoLink />
        {back ?? null}
      </div>

      {/* ONE ROW, NEVER A COLUMN (2026-10-06). This group was `min-w-0 shrink flex-wrap`,
          so on a phone narrower than the bar's ~365px of content — a 360px Android, or a
          zoomed browser — the screen's four glyphs wrapped inside a sliver between the
          flanks and stacked into a 4-high column, ~18% of the screen. It keeps its
          width and its row now; the bar's gaps tighten below `sm:` so it fits down to
          ~350px, and under that the whole group drops to its own full-width row under
          the logo and menu — two short rows, not a tower. */}
      <div className="flex shrink-0 flex-nowrap items-center justify-center gap-1 max-[349px]:order-last max-[349px]:basis-full sm:gap-2">
        {/* No default middle any more: the sections live in the ☰ at every width, so a
            page that injects nothing gets a quiet centre rather than a second nav. */}
        {center ?? null}
      </div>

      {/* The right edge, at every width: code bubble, the FAPS pill, then the ☰ ALL the
          way right — where the account bubble used to sit on a desktop. The account
          rides INSIDE the menu now (its top group), so the right edge is exactly
          [code][faps][☰] on a phone and a wall alike — the pill beside the menu that
          holds the account, the two "who you are" things adjacent, and the code chip
          keeping the slot people learned. The pill is absent (not empty) while FAPS is
          dark. */}
      {/* `sm:min-h` is the Faps pill's own height (py-1.5 + a 20px line + its border).
          The pill only exists once the settings answer, and it is 4px taller than the ☰,
          so the bar grew on every desktop load and pushed the whole page down with it.
          Below `sm` the pill is hidden, so phones are unchanged. */}
      <div className="flex flex-1 basis-0 items-center justify-end gap-1 sm:min-h-[2.125rem] sm:gap-2">
        {code ? <CodeBubbleChip code={code} /> : null}
        {faps ?? null}
        <SiteMenu account={account} extra={menu} />
      </div>
    </header>
  );
}

/**
 * "Code: XXXX" — this tab's claimable identity, worn openly.
 *
 * The label explains the mono glyphs beside it; below `lg:` the glyphs stand alone,
 * because on a phone the words cost more than the mystery. Pressing it opens the big
 * code card (readable across a room), which is also where "use this device as a screen"
 * lives for the person who wants to flip their own tab rather than be claimed.
 */
export const CODE_HINT =
  "This tab's remote code. Each tab is its own screen, so enter the code shown on the one you want to control.";

function CodeBubbleChip({ code }: { code: CodeBubble }) {
  /*
   * Two controls in one chip, because they answer two questions. The EYE masks the code
   * digits in the bar — for the moment a screen is being shared or filmed — and touches
   * nothing else; the CHIP BODY opens and closes the big code card on a screen, and on
   * every other page (`onOpen` set) it goes to /screen instead — the code in the bubble
   * is the code that page wears, so the click reads as "expand this". Local state for
   * the mask: it is a property of this bar, this page load, not of the screen, so nobody
   * else needs to know and a refresh simply shows the code again. Siblings rather than a
   * nested button, which HTML does not allow.
   */
  const [masked, setMasked] = useState(false);
  // The chip's one-line explanation, for the mouse (title) and the screen reader
  // (aria-describedby on the chip body) alike: people pairing a second tab typed the
  // first tab's code. Each tab is its own screen, and the code says which.
  const hintId = useId();
  return (
    <span
      className={cx(
        "flex items-center overflow-hidden rounded-full border border-white/15 text-sm transition-colors",
        code.shown ? "bg-white/15 text-white" : "bg-white/5 text-zinc-400",
      )}
    >
      <button
        type="button"
        onClick={() => setMasked((m) => !m)}
        aria-pressed={masked}
        aria-label={masked ? "Show the screen code in the bar" : "Hide the screen code in the bar"}
        title={masked ? "Show the code" : "Hide the code"}
        /* DESKTOP ONLY. Masking the code is a privacy affordance for a screen being
           streamed or screenshotted — a desk-and-capture-card act — and on a phone it
           cost a permanent 37px of the bar's tightest row for something almost nobody
           reaches for there. The code chip beside it still toggles the big card, so the
           phone loses no way to show or hide the code itself, only the in-bar mask. */
        className="hidden items-center self-stretch border-r border-white/10 px-2.5 transition-colors hover:bg-white/15 hover:text-white lg:flex"
      >
        {masked ? <IconEyeOff className="shrink-0" /> : <IconEye className="shrink-0" />}
      </button>
      <button
        type="button"
        onClick={code.onOpen ?? code.onToggle}
        // A navigation is not a toggle, so pressed-state only applies without onOpen.
        aria-pressed={code.onOpen ? undefined : code.shown}
        aria-label={
          code.onOpen
            ? "Open this tab as a screen"
            : code.shown
              ? "Hide the screen code"
              : "Show the screen code"
        }
        title={CODE_HINT}
        aria-describedby={hintId}
        className="flex items-center gap-1.5 py-1.5 pl-2 pr-2 transition-colors hover:bg-white/15 hover:text-white sm:pl-2.5 sm:pr-3"
      >
        <span id={hintId} className="sr-only">
          {CODE_HINT}
        </span>
        <span className="hidden whitespace-nowrap lg:inline">Code:</span>
        {/* The code is a live pairing code — whoever types it takes this screen — so it
            is masked in session replay like the Rooms invite link (lib/replayMask). The
            "Code:" label beside it stays readable. */}
        <span className="font-mono tracking-[0.2em] text-zinc-100" aria-hidden={masked} {...MASK}>
          {masked ? "•".repeat(Math.max(4, code.text.length)) : code.text}
        </span>
      </button>
    </span>
  );
}

/** The sections, in one place — inline above `lg:`, a menu below it, so the two can
 *  never drift into offering different things. */
/** Until when the Feed's menu row wears "New!" — a month after launch, the badge's
 *  per-surface rule (see NewBadge's header). */
const FEED_NEW_UNTIL = Date.UTC(2026, 9, 13);
/** Goon Rooms' month of "New!" (opened 2026-09-19). */
const ROOMS_NEW_UNTIL = Date.UTC(2026, 9, 19);
/** The shop's "New!". It has no launch date yet, so this allows a month or so past launch;
 *  move it if launch slips. */
const SHOP_NEW_UNTIL = Date.UTC(2027, 0, 1);

const NAV_LINKS: ReadonlyArray<{ to: string; label: string; icon: ReactNode; end?: boolean; badge?: ReactNode }> = [
  // The shop (2026-11-02) sits first, directly under Home, which SiteMenu renders just above
  // this list. Hidden until the server says it is open to this viewer.
  { to: "/shop", label: "Shop", icon: <IconBag />, badge: Date.now() < SHOP_NEW_UNTIL ? <NewBadge variant="inline" /> : null },
  // `end` stops this matching every nested route — without it /views/:id would light the
  // Views tab up while you are inside the editor.
  // The Feed (2026-09-13): the social surface above the watching ones. Hidden by
  // SiteMenu until /api/settings says the switch is on — a dark launch needs no deploy.
  { to: "/feed", label: "Feed", icon: <IconSparkle />, end: true, badge: Date.now() < FEED_NEW_UNTIL ? <NewBadge variant="inline" /> : null },
  // Goon Rooms (2026-09-19): watch together. Hidden until settings.rooms.enabled, like the Feed.
  { to: "/rooms", label: "Rooms", icon: <IconUser />, end: true, badge: Date.now() < ROOMS_NEW_UNTIL ? <NewBadge variant="inline" /> : null },
  { to: "/views", label: "Views", icon: <IconLayout />, end: true },
  { to: "/browse", label: "Browse", icon: <IconSearch /> },
  { to: "/collections", label: "Collections", icon: <IconStack /> },
  // The two ROLE links close the list — clicking either changes what this tab IS rather
  // than what it shows, so they sit together at the boundary before the guide, in the
  // order the old front-door picker taught: Screen, then Remote.
  { to: "/screen", label: "Screen", icon: <IconMonitor /> },
  { to: "/remote", label: "Remote", icon: <IconRemote /> },
];

/** The user guide, as the menu's own bottom group — help sits last, by convention, and
 *  outside NAV_LINKS so a future link doesn't land underneath it by accident. Named
 *  "How to goon", the footer's name for the same page (one entry, siteLinks.ts), so the
 *  menu and the footer call it one thing. Every surface's bar is this SiteMenu, so it
 *  is in the ☰ everywhere the ☰ is. */
const GUIDE_LINK = { to: HOW_TO_LINK.to, label: HOW_TO_LINK.label, icon: <IconBook /> } as const;

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cx(
    "rounded-md px-2.5 py-1.5 text-sm transition-colors",
    isActive ? "bg-[var(--color-panel-2)] text-zinc-100" : "text-zinc-400 hover:text-zinc-100",
  );

/**
 * THE nav menu — the three-line button at the bar's far right, at every width, with the
 * code bubble sitting just left of it. It used to be the phone's collapsed fallback
 * while a desktop got inline links and a separate account bubble; it is the only
 * navigation now, in a fixed order:
 *
 *   account group (profile / sign-in / premium)   — `account`
 *   Shop · Views · Browse · Collections · Remote   — NAV_LINKS
 *   How to goon                                    — GUIDE_LINK
 *   page-injected rows (a screen's settings…)      — `extra`
 *
 * No legal row: Terms · Privacy · DMCA · 2257 · Contact live in SiteFooter only — the
 * fine print is the footer's job, not the nav's.
 *
 * One menu that takes entries beats each surface growing its own — three links laid out
 * inline measured 85px of an 844px phone, a permanent 10%, and every control that
 * follows them would have paid the same rent. The desktop paid it too, plus a whole
 * account bubble; now every width pays for one ☰.
 *
 * Dismissal matches AccountMenu (outside pointer-down, Escape): two transient surfaces
 * in the same bar that close on different gestures is the kind of difference nobody can
 * articulate but everybody feels.
 */
/**
 * THE ROW SHAPE, for every entry in the ☰ wherever it is written — the account group
 * (MenuAccountItems), the sections below, and a page's own rows (ScreenMenuItems). It was
 * three copies of the same class string in three files; a menu whose rows are declared
 * apart is a menu whose rows drift apart, and the icon column below is exactly the kind of
 * change that would have landed in one of the three.
 *
 * NO TEXT COLOUR HERE, on purpose: a NavLink wants zinc-100 when it is the page you are
 * on and zinc-300 when it is not, and `cx` is a plain join — two `text-*` classes in one
 * attribute are settled by stylesheet order, not string order, so the caller states the
 * one it wants.
 */
export const MENU_ROW =
  "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-[var(--color-panel-2)]";

/**
 * The row's leading glyph — what the eye lands on before it reads anything.
 *
 * FIXED 20px WHATEVER IT HOLDS. Every label in the menu starts on the same x, so the list
 * reads as one column of words with a column of marks beside it rather than a ragged edge;
 * the Gold badge (the one row whose mark is a filled pill, not a 16px stroke) simply
 * overhangs its slot by a pixel and a half each side instead of shoving "Premium" right of
 * everything else.
 *
 * MUTED, and it stays muted — hover and the active page brighten the WORD. These are
 * signposts, not the content of the row, and a glyph that tracked every state would put
 * two things in motion where one is being asked about.
 */
export function MenuRowIcon({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden className="flex w-5 shrink-0 items-center justify-center text-zinc-500">
      {children}
    </span>
  );
}

/** One row of the ☰ — a NavLink in the menu's own type. */
function MenuNavLink({
  to,
  end,
  label,
  icon,
  badge,
}: {
  to: string;
  end?: boolean;
  label: string;
  icon: ReactNode;
  badge?: ReactNode;
}) {
  /*
   * THE PAGE YOU ARE ON IS NOT A NEW PLACE. React Router only replaces on its own when the
   * target is the exact current URL, so Screen pressed on `/screen?view=…` (the arrows and
   * the picker write `?view=` in place) PUSHED a bare `/screen` — same route, no remount,
   * nothing changed on the glass — and every press left one more entry for Back to wade
   * through before it reached the page you came from (QA 2026-10-05). Same pathname
   * replaces instead. On /screen (and Home `/`) the query rides along too: it is what is
   * playing (see PendingScreen's onForked), and dropping it would make a reload play
   * something else.
   */
  const { pathname, search } = useLocation();
  const here = pathname === to;
  const keepQuery = here && (to === "/screen" || to === "/");
  return (
    <NavLink
      to={keepQuery ? { pathname: to, search } : to}
      replace={here}
      end={end}
      role="menuitem"
      className={({ isActive }) => cx(MENU_ROW, isActive ? "text-zinc-100" : "text-zinc-300")}
    >
      <MenuRowIcon>{icon}</MenuRowIcon>
      {label}
      {badge}
    </NavLink>
  );
}

function HomeMenuLink() {
  const href = homeHref();
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const search = href.includes("?") ? href.slice(href.indexOf("?")) : "";
  return (
    <NavLink
      to={{ pathname: "/", search }}
      replace={onHome}
      end
      role="menuitem"
      className={({ isActive }) => cx(MENU_ROW, isActive ? "text-zinc-100" : "text-zinc-300")}
    >
      <MenuRowIcon>
        <IconHome />
      </MenuRowIcon>
      Home
    </NavLink>
  );
}

function SiteMenu({ account, extra }: { account?: ReactNode; extra?: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Hidden until the one settings fetch says on: a link to a 503 is worse than none.
  // Chained the whole way down, not just past `settings`: a payload that predates either
  // flag (an old server still answering a new bundle, mid-deploy) has no `feed` object at
  // all, and `.enabled` on undefined took the whole menu down with a TypeError.
  const settings = usePublicSettings();
  const feedOn = settings?.feed?.enabled === true;
  const roomsOn = settings?.rooms?.enabled === true;
  const shopOn = useShopOpen();
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /*
   * A MENU FOR THE KEYBOARD TOO (QA 2026-10-21): opening moves focus to the first row,
   * Tab / Shift+Tab cycle the rows (they used to walk off the last one into the page
   * behind), and closing from inside — Esc, or pressing a row — hands focus back to the
   * ☰. Synchronously, before the rows unmount: a dialog a row opens (sign in, settings…)
   * then captures the ☰ as its opener and returns there on its own close.
   */
  const closeToButton = () => {
    setOpen(false);
    buttonRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return;
    let target = focusablesIn(menu)[0];
    target?.focus({ preventScroll: true });
    // A row that renders a beat late (Premium waits on the settings fetch) can land
    // above the one just focused: follow the first row until the reader moves.
    const watch = new MutationObserver(() => {
      const first = focusablesIn(menu)[0];
      if (first && first !== target && document.activeElement === target) {
        target = first;
        first.focus({ preventScroll: true });
      }
    });
    watch.observe(menu, { childList: true, subtree: true });
    return () => watch.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeToButton();
        return;
      }
      if (e.key !== "Tab" || !menuRef.current) return;
      const items = focusablesIn(menuRef.current);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && menuRef.current.contains(active);
      if (!inside || (e.shiftKey ? active === first : active === last)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-site-menu-button
        aria-label="Menu"
        aria-expanded={open}
        aria-haspopup="menu"
        className={cx(linkClass({ isActive: false }), "inline-flex items-center")}
      >
        <IconMenu width="18" height="18" />
      </button>
      {open && (
        /* right-0, not left-0: the button is at the bar's right edge now, and a menu
           that opens off the screen's far side is a menu that opens nowhere. */
        <div
          ref={menuRef}
          role="menu"
          onClick={closeToButton}
          /* SHORT SCREENS (QA 2026-10-21, 844×390 landscape): the menu is capped at the
             screen below the bar and scrolls inside itself, so the wheel, a drag and Tab
             reach Remote and How to goon instead of scrolling the page
             behind it. overscroll-contain keeps the scroll from chaining to the page. */
          className="absolute right-0 mt-1 max-h-[calc(100dvh-3.5rem)] w-52 overflow-y-auto overscroll-contain rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] py-1 shadow-xl"
        >
          {/* Who you are, first — the group that lived in the desktop bubble. */}
          {account ? (
            <>
              {account}
              <div aria-hidden className="my-1 h-px bg-[var(--color-edge)]" />
            </>
          ) : null}
          {/* Home restores the last front-door mode (logo does the same). Absent from
              NAV_LINKS because the href is dynamic — last ?view=, else bare `/`. */}
          <HomeMenuLink />
          {NAV_LINKS.map((l) =>
            (l.to === "/feed" && !feedOn) || (l.to === "/rooms" && !roomsOn) || (l.to === "/shop" && !shopOn) ? null : (
              <MenuNavLink key={l.to} to={l.to} end={l.end} label={l.label} icon={l.icon} badge={l.badge} />
            ),
          )}
          <div aria-hidden className="my-1 h-px bg-[var(--color-edge)]" />
          <MenuNavLink to={GUIDE_LINK.to} label={GUIDE_LINK.label} icon={GUIDE_LINK.icon} />
          {/* The page's own rows, last, ON THEIR OWN GROUND: a nav row takes you
              somewhere, these act HERE (switch view, screen settings…), and the tint
              says so before the reading does. `-mb-1 pb-1` runs the ground to the
              menu's bottom edge through the container's own py-1. */}
          {extra ? (
            <>
              <div aria-hidden className="my-1 mb-0 h-px bg-[var(--color-edge)]" />
              <div className="-mb-1 bg-white/[0.06] pb-1 pt-1">{extra}</div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
